import servicesUtils from '../utils/service'

/**
 * 素材庫 — 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md`
 * （形狀看 §2.2，端點看 §5.2，上限看 §5.3）。
 *
 * ⚠ 這裡照本 repo 慣例 `catch → log → throw`，**呼叫端必須自己 catch**。
 * 既有元件多半沒接，API 失敗時老師只看到按鈕按了沒反應（規格 §9）。
 * **素材庫的新元件不沿用這個缺口**——每一個呼叫端都要接，而且要接得出差別：
 * `err.status` 帶得到 HTTP 狀態碼（403 = 配額滿、409 = 下架沒帶 confirmed、400 = 參數錯）。
 *
 * ⚠ **每一支都傳 `{ handleError: true }`，這不是可以省的。** `utils/service.js` 的
 * `checkStatus` **預設對任何非 2xx 都 `router.push` 換頁**（400／403／500 一律去
 * `/error`），呼叫端的 catch 跑的時候畫面已經換掉了——`err.status` 拿得到也沒用。
 * 傳了它才會純 throw，錯誤才顯示得在對話框裡。
 *
 * ⚠ **401 例外：仍然會換頁去重新登入**，那是對的（session 過期不是對話框能解決的事）。
 * 所以呼叫端不需要、也不該自己處理 401。
 */

/** 上傳與 YouTube 登錄共用的欄位（`uploadMedia` 多一個 file，`addYoutubeMedia` 多一個 url）。 */
function newMediaFields(payload) {
    return {
        title: payload.title,
        description: payload.description,
        share_scope: payload.shareScope,
        rights_agreed: payload.rightsAgreed,
        rights_note: payload.rightsNote,
    }
}

/**
 * 拆掉 `BaseAction` 的信封。
 *
 * ⚠ **後端每一支端點都回 `{ data, err }`**（`ApiResponseEntity`），真正的內容在 `data`。
 *
 * ⚠ **這裡與 repo 既有的 service 不一樣**：既有的（`templateUnitServices`、`diaryServices`）
 * 直接回整包，呼叫端自己寫 `res?.data?.x`。素材庫這一組選擇在 service 拆掉——
 * 呼叫端不該知道信封的存在，而且漏拆一次的症狀是**靜默的空白**
 * （`res.items` 是 undefined，選擇器就一直是空的，沒有任何錯誤訊息）。
 * 那個坑 2026-08-28 真的踩到了。
 *
 * ⚠ 只在 2xx 會走到這裡：非 2xx 時 `checkStatus` 會 throw（每一支都傳了 handleError），
 * 所以不必檢查 `err`——`BaseAction` 設 `err` 時一定同時設了非 2xx 的狀態碼。
 */
function unwrap(res) {
    return res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
}

/** 每一支都要的選項，見檔頭。抽出來是為了不要有人漏傳。 */
const HANDLE_ERROR = { handleError: true }

export const mediaServices = {
    /**
     * 素材庫的瀏覽／挑選清單。
     *
     * ⚠ **`purpose` 是必填的**，而且沒有「列出全部素材」的用法（規格 §1）：素材庫永遠是
     * 「為某個欄位挑東西」。查 `hotspot` 不會撈到 `cover`——挑找不同圖時看到沒有熱區的
     * 封面照，選下去遊戲當場壞掉。
     *
     * 回傳的**可見範圍是後端算出來的**，不是欄位（規格 §2.3）：官方素材 + 自己的（不論
     * share_scope / review_status）+ 別人的 `public` 且 `approved`。已下架（`delisted`）
     * 與還在轉檔（`process_status != 'ready'`）的一律不出現。
     *
     * @param {Object} query
     * @param {string} query.purpose 必填。`cover` / `image` / `hotspot` / `pdf` / `video`
     *   / `wordsearch` / `colorword`。不認識的值後端回 400（不是空清單）。
     * @param {string} [query.lang] 給值時**會連 `lang IS NULL` 的通用素材一起撈**，
     *   所以「依編輯者語言過濾」不會把無語言的素材濾掉。不給 = 不限語言。
     * @param {string} [query.scope] `official` / `mine` / `public`。不給 = 三個分支全開。
     *   ⚠ **沒有 `team`**，team 共用已於 2026-08-20 取消。
     * @param {string} [query.q] 標題／說明的關鍵字，上限 100 字。
     * @param {string} [query.visibility] 公開狀態：`private` / `pending` / `shared`
     *   / `rejected`。不給 = 不限。⚠ **只有 `scope=mine` 時生效**（後端擋）。
     *   ⚠⚠ 三個 `public` 的值**各自還比 review_status**——`shared` 不會包含還在審與
     *   被退回的，而那兩種別人根本看不到。值打錯回 400。
     * @param {string} [query.order] 排序：`latest`（預設）/ `title` / `size`。
     *   ⚠ 值打錯回 400。⚠ 沒有「使用次數」——那個數字目前不存在。
     * @param {string} [query.provider] 影片來源：`youtube` 或 `uploaded`。不給 = 不限。
     *   ⚠ `uploaded` 是「**不是** YouTube」，**包含沒有 `provider` 的列**——手動 insert
     *   的官方影片就是那個形狀，當成等值比對的話一按篩選它們就整批消失。
     *   ⚠ **只對 `purpose = 'video'` 有意義**，別的分類不要送（送了不會壞，但沒用）。
     *   ⚠ 值打錯後端回 **400**，不是空清單——那是刻意的，免得看起來像「沒有素材」。
     * @param {number} [query.page] 1 起，預設 1。
     * @param {number} [query.perPage] 預設 30，上限 100（超過後端回 400）。
     * @param {boolean} [query.includeDelisted] 連**已下架**的一起列出。
     *   ⚠ **只有「我的素材」頁該給 true**（後端也只在 `scope=mine` 時理它）。
     *   挑選器不能給——已下架的素材不該能被挑。回傳的列會帶 `status`。
     * @returns {Promise<{total:number, page:number, per_page:number, items:Array}>}
     *   每個 item：`{ media_id, kind, purpose, title, description, url, width, height,
     *   duration_ms, attributes, lang, owner_type, share_scope, review_status, is_mine }`
     *   ⚠ `attributes` 依 `purpose` 形狀不同（規格 §2.2）：`hotspot` 有 `targets[]`、
     *   字表有 `words[]`、`video` 有 `provider`，其餘是 `{}`。
     *   ⚠ **遇到不認識的 `purpose` / `kind` 不要報錯**（規格 §2.5）——那是擴充性的硬規則。
     */
    async getMediaList(query) {
        try {
            const params = servicesUtils.omitNullish({
                purpose: query.purpose,
                lang: query.lang,
                scope: query.scope,
                q: query.q,
                page: query.page,
                /// 後端讀的是 snake_case 的 `per_page`
                per_page: query.perPage,
                /// ⚠ 連還在處理的素材一起列出（規格 §6.3）。**後端只在 `scope=mine`
                /// 時理它**，其他 scope 會忽略——所以這裡照送不必自己判斷。
                /// 回傳的列要靠 `process_status` 自己標成不可選。
                include_processing: query.includeProcessing ? '1' : null,
                /// ⚠ **連已下架的一起列出。只有「我的素材」頁該送這個。**
                /// 後端只在 `scope=mine` 時理它。它存在的唯一理由是「重新上架」按不到
                /// ——預設查詢只撈 active，所以下架之後那份素材就從清單消失了。
                /// ⚠⚠ **挑選器絕對不能送**：已下架的素材不該能被挑（規格 §2.3），
                /// 那正是下架的效果之一。
                include_delisted: query.includeDelisted ? '1' : null,
                provider: query.provider || null,
                visibility: query.visibility || null,
                order: query.order || null,
            })
            const url = `${servicesUtils.resourceUrl('media')}`
                + '?' + servicesUtils.queryParams(params)
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getMediaList err: ${err}`)
            throw err
        }
    },

    /**
     * 「我的素材」每個分類各有幾筆。
     *
     * ⚠ **它存在的理由**：`getMediaList` 的 `purpose` 是必填單值，所以要在分類 tab
     * 與 chips 上顯示筆數，不用這一支就得為 7 個分類各打一次 request。
     *
     * ⚠⚠ **筆數會跟著 `q` / `visibility` 一起變，所以這兩個參數必須與清單查詢同步。**
     * 有搜尋條件時顯示「總筆數」會與清單自相矛盾（chip 說 4、清單只有 1）；
     * 而「每個分類各有幾筆符合」反而是老師最需要的——他常常不確定要找的檔案
     * 在哪一個分類。**改清單的篩選條件時記得一起改這裡。**
     *
     * ⚠ 後端與清單**共用同一份 WHERE**，所以數字與清單內容一定對得上（已驗）。
     * ⚠ 固定 `scope=mine`（後端寫死，不收參數）。
     *
     * @param {Object} [query]
     * @param {string} [query.q] 與清單相同的關鍵字。
     * @param {string} [query.visibility] 與清單相同的公開狀態。
     * @returns {Promise<{counts:Object<string,number>, video_youtube:number,
     *   video_uploaded:number}>}
     *   ⚠ **`counts` 裡沒有資料的分類不會出現**（`GROUP BY` 的結果），讀的時候要當 0。
     *   ⚠ 影片的兩個數字是**來源**的細分，因為影片那一類的 chips 是依來源分的；
     *   `video_uploaded` 含**沒有 `provider`** 的列（手動 insert 的官方影片）。
     */
    async getMediaCounts(query = {}) {
        try {
            const params = servicesUtils.omitNullish({
                q: query.q || null,
                visibility: query.visibility || null,
            })
            const url = `${servicesUtils.resourceUrl('mediaCounts')}`
                + '?' + servicesUtils.queryParams(params)
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getMediaCounts err: ${err}`)
            throw err
        }
    },

    /**
     * 目前使用者自己的已用容量與上限。
     *
     * ⚠ **是每個使用者各自 2GB，沒有共用池**，而且**不含官方素材**（規格 §5.3）。
     * @returns {Promise<{used_bytes:number, limit_bytes:number, remaining_bytes:number}>}
     */
    async getMediaQuota() {
        try {
            const url = `${servicesUtils.resourceUrl('mediaQuota')}`
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getMediaQuota err: ${err}`)
            throw err
        }
    },

    /**
     * 上傳素材（圖片 / PDF / 影片）。`multipart`。
     *
     * ⚠ **權利聲明沒勾就回 400 且不寫任何列**（規格 §5.3）。前端也要 disable 送出按鈕，
     * 但這裡不代擋——擋在 UI 是為了體驗，擋在後端才是規則。
     * ⚠ **`purpose` 決定大小與副檔名的上限**：圖 10MB、PDF 20MB、影片 500MB 且 ≤10 分鐘。
     *   數字**以後端 `MediaConstants` 為準**，前端那份只是為了不白傳。
     * ⚠ **字表（`wordsearch` / `colorword`）不能走這一支**，它們沒有檔案（後端回 400）。
     * ⚠ **找不同（`purpose = 'hotspot'`）必須送 `hotspotRect`**：`{x,y,w,h}` 的
     *   **比例**（0–1），後端寫成 `attributes.targets`。沒送後端回 400——沒有熱區的
     *   找不同圖是一條死路（挑得到但存不了，而且無法補救）。
     *   ⚠ **不要送像素**：`processImage` 會把長邊縮到 1600、`.rotate()` 會依 EXIF
     *   把長寬互換，所以瀏覽器量到的像素與存進 DB 的 `width`/`height` 不同空間。
     * ⚠ **PDF 會多送一個 `poster`**：第一頁算繪出的 JPEG，後端寫進
     *   `attributes.poster_url`（**與影片共用同一個 key**）。算不出來就不送，
     *   後端照樣建立素材、只是沒有縮圖。其他 kind 不要送——後端只在 PDF 讀它。
     * ⚠ **影片上傳完不等於可用**：回來的 `process_status` 是 `pending`，真正的轉檔由 cron
     *   觸發，所以 UI 要顯示「處理中」而不是「完成」（規格 §6.3）。轉檔完成前它**不會**
     *   出現在 `getMediaList` 裡。貼 YouTube 連結走 `addYoutubeMedia`，不走這裡。
     *
     * @param {Object} payload
     * @param {string} payload.purpose
     * @param {File} payload.file
     * @param {string} payload.title 上限 200 字
     * @param {string} [payload.description] 上限 500 字
     * @param {string} [payload.shareScope] `private` / `public`，預設 `private`。
     *   ⚠ 選 `public` 時後端**同時**寫 `review_status = 'pending'`，但**上傳者自己照樣
     *   選得到**（規格 §2.3）。UI 的文案必須把這兩件事一起說（§7.3）。
     * @param {boolean} payload.rightsAgreed
     * @param {string} [payload.rightsNote] 來源說明，上限 500 字
     * @returns {Promise<{media_id:number, kind:string, purpose:string, url:string,
     *   width:number|null, height:number|null, byte_size:number,
     *   share_scope:string, review_status:string}>}
     *   配額滿時 throw，且 `err.status === 403`。
     */
    async uploadMedia(payload) {
        try {
            const url = `${servicesUtils.resourceUrl('media')}`
            /// ⚠ multipart 的欄位一律變成字串，`false` 會變 `'false'`。後端只認明確的
            /// 真值（'true' / '1' / 'on' / 'yes'），所以送布林是安全的——但**不能送
            /// nullish**，那會變成字串 `"null"`，所以先過 omitNullish。
            const body = servicesUtils.omitNullish(Object.assign({
                file: payload.file,
                purpose: payload.purpose,
                /// PDF 的第一頁縮圖（前端算繪）。⚠ **`omitNullish` 會把 null 濾掉**，
                /// 所以算不出來時這個欄位根本不會出現在 multipart 裡——後端的
                /// `request.file('poster')` 拿到 null，走「沒有縮圖」那條路。
                poster: payload.poster,
                /// 找不同的熱區框。⚠ **送 JSON 字串**：multipart 的欄位不能是物件。
                /// ⚠ 內容是**比例（0–1）**而不是像素——後端會乘上它自己算出的輸出尺寸
                /// （`processImage` 會縮圖、`.rotate()` 會依 EXIF 把長寬互換）。
                /// ⚠ `omitNullish` 會濾掉 null，所以非 hotspot 時這個欄位不會出現。
                hotspot_rect: payload.hotspotRect
                    ? JSON.stringify(payload.hotspotRect)
                    : null,
            }, newMediaFields(payload)))
            return unwrap(await servicesUtils.postFormData(url, body, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to uploadMedia err: ${err}`)
            throw err
        }
    },

    /**
     * 把一支 YouTube 影片登錄成素材。**沒有檔案、不轉檔、不佔配額。**
     *
     * ⚠ **後端會把網址正規化成 `https://www.youtube.com/watch?v=<ID>`**，而且**收得很寬**：
     * `watch?v=` / `youtu.be/` / `embed/` / `shorts/` / `live/` 都認得，`m.` 手機網域與
     * 後面掛著 `&t=` 的時間戳也都行（後端 B5 的 12 項驗證）。所以 UI **不需要**提示老師
     * 「要貼哪一種網址」，也不該自己先做轉換。認不出 ID 的網址回 400。
     * ⚠ **權利聲明照樣要勾**——貼別人的影片一樣有版權問題，而且比上傳更容易做。
     * ⚠ 回來的 `url` 是正規化後的那一個，**不是**使用者貼的那一個。要顯示就顯示回傳值。
     *
     * @param {Object} payload
     * @param {string} payload.url 使用者貼的原始網址（上限 1000 字）
     * @param {string} payload.title
     * @param {string} [payload.description]
     * @param {string} [payload.shareScope] `private` / `public`，預設 `private`
     * @param {boolean} payload.rightsAgreed
     * @param {string} [payload.rightsNote]
     * @returns {Promise<{media_id:number, kind:string, purpose:string, url:string,
     *   share_scope:string, review_status:string}>}
     */
    async addYoutubeMedia(payload) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaYoutube')}`
            const body = servicesUtils.omitNullish(Object.assign({
                url: payload.url,
            }, newMediaFields(payload)))
            return unwrap(await servicesUtils.post(url, body, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to addYoutubeMedia err: ${err}`)
            throw err
        }
    },

    /**
     * 建立一份字表素材（找單字 / 講顏色）。**沒有檔案、不佔配額、不轉檔。**
     *
     * ⚠ **這不是上傳。** 字表的內容整包在 `attributes.words`，所以走 JSON 而不是
     * multipart，`uploadMedia` 收到字表的 purpose 會被後端回 400。
     *
     * ⚠ **`words` 的規則依 `purpose` 而不同**，而且**三個限制的後果都是靜默的**
     * （不報錯、不過關），所以送出前要先用 `checkWords` 擋一次：
     *
     * | | 找單字 | 講顏色 |
     * |---|---|---|
     * | 字元 | **只能 A–Z**（格子的填充字元是大寫 A–Z，其他字元一眼看出答案） | 不限 |
     * | 長度 | **2 – 15**（1 個字母永遠選不起來；> 15 放不進最大的格子） | 1 – 24 |
     * | 大小寫 | 後端一律轉大寫 | 原樣保留 |
     *
     * ⚠ **`lang` 不要送**（2026-09-01：字表只支援英文，沒有可分的東西）。後端收得到它，
     * 但那是留給手動建官方字表的路徑用的。目前沒有任何介面路徑會填 lang，所以挑選器的
     * 語系過濾**整個拿掉了**——留著只會把手動 insert 時填了 lang 的素材藏起來。
     * ⚠ 真要送的話值域是 `zh` / `en` / `ja`（DDL 是 `varchar(3)`），**`zh-tw` 會直接 400**。
     *
     * ⚠ **權利聲明照樣要勾**：字表可能是從教科書某一課整理出來的，而公開的字表要送審，
     * 審核的人需要看得到來源說明。
     *
     * @param {Object} payload
     * @param {string} payload.purpose `wordsearch` / `colorword`
     * @param {string[]} payload.words 單字陣列（後端也收換行／逗號分隔的字串）
     * @param {string} payload.title 上限 200 字
     * @param {string} [payload.description] 上限 500 字
     * @param {string} [payload.lang] ⚠ 介面不送，見上面。不給 = 通用素材（`lang IS NULL`）
     * @param {string} [payload.shareScope] `private` / `public`，預設 `private`
     * @param {boolean} payload.rightsAgreed
     * @param {string} [payload.rightsNote]
     * @returns {Promise<Object>} 形狀與 `QuestMediaBrief` 一致（含 `attributes.words`）
     */
    async addWordlistMedia(payload) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaWordlist')}`
            const body = servicesUtils.omitNullish(Object.assign({
                purpose: payload.purpose,
                words: payload.words,
                lang: payload.lang,
            }, newMediaFields(payload)))
            return unwrap(await servicesUtils.post(url, body, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to addWordlistMedia err: ${err}`)
            throw err
        }
    },

    /**
     * 改寫一份字表的內容。**只改 `words`**，標題／說明／可見範圍走 `updateMedia`。
     *
     * ⚠⚠ **這是素材庫裡唯一「內容可以就地改變」的路徑，而已經在用它的題目會立刻跟著變。**
     * 其他素材的內容（檔案）是不可變的，換檔案＝換一筆素材，所以那些路徑沒有這個問題。
     *
     * ⚠ **所以這一支會失敗，而且失敗是正常的。** 後端會對每一道還活著的引用題目重驗：
     *
     * | 檢查 | 沒擋住會怎樣 |
     * |---|---|
     * | `target_count ≤ 字數` | 學生要找 5 個字但字表只剩 3 個 → **遊戲永遠玩不完** |
     * | `max(字長) ≤ grid_size` | 那個字靜默地不在格子裡 → 學生找不到 |
     *
     * 任一項不過就**整批拒絕**（400），訊息裡會指名是哪一道題目。UI 要把它顯示出來，
     * 不能當成一般失敗——老師需要知道要先去改哪一題。
     *
     * ⚠ **講顏色的字表不會因為引用而被擋**：它的題目設定只有 `timer`。
     *
     * @param {number} mediaId
     * @param {string[]} words
     * @returns {Promise<Object>} 形狀與 `QuestMediaBrief` 一致
     */
    async updateWordlistWords(mediaId, words) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaItemWordlist')}`
                .replace(':mediaId', mediaId)
            /// ⚠ **不能過 omitNullish**：words 是必填的，漏掉時要讓後端回 400
            /// 而不是靜默送一個空 body（那會被當成「改成空字表」還是「沒帶」很難分）。
            return unwrap(await servicesUtils.post(url, { words: words }, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to updateWordlistWords err: ${err}`)
            throw err
        }
    },

    /**
     * 改素材的標題 / 說明 / 可見範圍。**只有這三個欄位可以改。**
     *
     * ⚠ `purpose` / `kind` / `lang` / `attributes` 刻意不開放：`purpose` 決定素材能放在
     * 哪個欄位，改了會讓已經引用它的題目拿到型別不對的東西。要換用途就重新上傳一筆。
     * ⚠ **只有自己的素材可以改**，官方素材與別人的素材回 403。
     * ⚠ **`private → public` 會把 `review_status` 變成 `pending`**（反過來變回 `none`），
     * 這是後端自己做的，前端不要送 `review_status`。送同一個值不會把已核准的打回 pending。
     *
     * ⚠⚠ **`description` 一律會被覆寫，沒帶就是清空。** 這是後端目前的行為：
     * `UpdateMediaAction` 對「沒帶」與「帶空字串」都算出 `null`，而 service 只檢查
     * `!== undefined`，所以 `row.description = null` 照樣執行。`title` 與 `share_scope`
     * 有 `!== null` 的保護，**`description` 沒有**。
     * **所以只改標題時也要把目前的 description 一起送回去**，否則老師改個名字就把說明弄丟了。
     * （要讓「沒帶 = 不動」真的成立，得改後端去讀 raw body 的 key 是否存在——`UpdateMediaAction`
     * 自己的註解已經記了這件事。那是 B4 的票，不是這裡能繞過的。）
     *
     * @param {number} mediaId
     * @param {Object} payload `{ title?, description?, shareScope? }`
     * @returns {Promise<{media_id:number, title:string, description:string|null,
     *   share_scope:string, review_status:string}>}
     */
    async updateMedia(mediaId, payload) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaItem')}`
                .replace(':mediaId', mediaId)
            /// 用 POST 不是 PUT/PATCH：全站慣例（規格 §5）
            return unwrap(await servicesUtils.post(url, servicesUtils.omitNullish({
                title: payload.title,
                description: payload.description,
                share_scope: payload.shareScope,
            }), HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to updateMedia err: ${err}`)
            throw err
        }
    },

    /**
     * 改寫找不同圖的熱區（**取代**成一個新的矩形）。
     *
     * ⚠ **只有自己的素材**：官方素材與別人的素材回 403。
     * ⚠ **`rect` 是比例（0–1）而不是像素**——與上傳的契約一致，同一份驗證規則。
     *   編輯時不存在縮圖／EXIF 轉向的落差（老師看的就是已經存起來的那張圖）。
     * ⚠ **超過一個熱區的素材會被拒絕**（400）：這個介面只框一個，存下去會把其他的
     *   弄掉。官方的 5 熱區圖本來就因為 403 進不來，會遇到的只有手動 insert 的多熱區
     *   使用者素材。
     *
     * ⚠ **與 `updateWordlistWords` 的關鍵差別：這一支不會因為別人的題目而失敗。**
     * 改字表會讓「要找 5 個字但只剩 3 個」出現，所以那邊會重驗每一道引用的題目；
     * 移動熱區框只是改變學生要點的位置，任何引用它的題目都還是合法的。
     *
     * @param {number} mediaId
     * @param {{x:number,y:number,w:number,h:number}} rect 比例 0–1
     * @returns {Promise<Object>} 形狀與 `QuestMediaBrief` 一致
     */
    async updateHotspotRect(mediaId, rect) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaItemHotspot')}`
                .replace(':mediaId', mediaId)
            /// ⚠ **不過 omitNullish**：`rect` 是必填的，漏掉要讓後端回 400
            /// 而不是靜默送一個空 body。
            return unwrap(await servicesUtils.post(url, { rect: rect }, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to updateHotspotRect err: ${err}`)
            throw err
        }
    },

    /**
     * 下架前先查影響範圍：會被停用的題目與單元，以及**其中有幾筆不是你的**。
     *
     * ⚠ **`not_mine_quest_count` 是這支端點存在的主要理由**（規格 §5.2）。素材是下架者的、
     * 最終決定權歸他，但二次確認的對話框**一定要講出這個數字**——否則他不知道自己正在
     * 停用別人的課程。
     * ⚠ **沒人用的素材回全 0 的空清單，不是 404。** 空集合不要當成錯誤處理。
     * ⚠ **不存在的 mediaId 回 404**，而 404 是 `checkStatus` 會換頁的兩個碼之一——
     * 傳了 `handleError` 之後不換頁了，但仍然要確保 `mediaId` 來自 `getMediaList` 的結果，
     * 不要讓使用者輸入。
     * ⚠ 對照表（`diary_quest_media`）是後端 B8 在存題目時才開始寫的，所以 **B8 上線前
     * 這支一律回空——那不是壞掉**。
     *
     * @param {number} mediaId
     * @returns {Promise<{media_id:number, quest_count:number, not_mine_quest_count:number,
     *   unit_count:number, homeworks:Array<{diary_homework_id:number, is_mine:boolean,
     *   quests:Array<{quest_id:number, quest_title:string, unit:number|null}>}>}>}
     */
    async getMediaImpact(mediaId) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaImpact')}`
                .replace(':mediaId', mediaId)
            return unwrap(await servicesUtils.get(url, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to getMediaImpact err: ${err}`)
            throw err
        }
    },

    /**
     * 下架素材：**連帶停用所有引用它的題目、以及因此空掉的單元**，並發通知（規格 §2.4.3）。
     *
     * ⚠ **這不是「新題目選不到而已」。** 老師的課程會當場停用，所以一定要先呼叫
     * `getMediaImpact` 做二次確認，把題目數、單元數、以及「其中 M 筆不是你的」攤開來看。
     * ⚠ **`confirmed: true` 是必要的**——沒帶後端回 409（`err.status === 409`）。
     * 後端會在 409 的 body 附上 impact，但 `checkStatus` 在讀 body 之前就 throw 了，
     * **所以那份 payload 拿不到**。不要指望它：影響範圍一律靠先打 `getMediaImpact` 拿。
     * ⚠ **下架不會讓檔案變成不可存取**（S3 是 public-read），效果是「庫裡挑不到、用到它的
     * 題目被停用」。不要用「檔案已刪除」之類的文案。
     * ⚠ 可以用 `relistMedia` 復原（規格 §2.4.4）——文案應該說得出這一點。
     *
     * ⚠ **後端 B11 已完成並驗過**（2026-09-06，`MediaLibrary.verify-b11.md`）。
     *   剩下的是 F12 的呈現：二次確認對話框（必須顯示 impact 的三個數字，尤其
     *   **「其中 M 筆不是你的」**）與成功後的結果。
     * ⚠ 回傳 `{ media_id, blocked_quest_count, blocked_unit_count, affected_homework_ids }`。
     * ⚠ **通知在後端非同步送出**（B12，2026-09-09）：受影響範本的擁有者會收到站內訊息
     *   與 email，下架者自己也會收到一份摘要。**端點不等通知寄完就回應**，所以這一支
     *   成功不代表信已經寄到——不要在 UI 上斷言「已通知」，只說「會收到」。
     *
     * @param {number} mediaId
     */
    async delistMedia(mediaId) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaItem')}`
                .replace(':mediaId', mediaId)
            /// DELETE 沒被慣例排除（排除的只有 PUT / PATCH），且它帶 body——見 utils/service.js
            return unwrap(await servicesUtils.delete(url, { confirmed: true }, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to delistMedia err: ${err}`)
            throw err
        }
    },

    /**
     * 重新上架：被這份素材卡住的題目與單元**會自動恢復**（規格 §2.4.4）。
     *
     * ⚠ **一題可能被多份素材同時卡住**（例如翻卡牌用了兩張圖、兩張都被下架），
     * 只恢復其中一份時那題**還不會**開放。UI 要說得出還缺哪一份，不要顯示「已全部恢復」。
     *
     * ⚠ **後端 B11 已完成並驗過**（2026-09-06）。回傳
     *   `{ media_id, restored_quest_count, still_blocked_quest_count, restored_unit_count,
     *   affected_homework_ids }`。
     * ⚠⚠ **`still_blocked_quest_count` 一定要顯示出來**——它就是上面那句「還缺哪一份」的
     *   數字來源。顯示「已全部恢復」而它不是 0，就是在騙老師。
     *
     * @param {number} mediaId
     */
    async relistMedia(mediaId) {
        try {
            const url = `${servicesUtils.resourceUrl('mediaRelist')}`
                .replace(':mediaId', mediaId)
            return unwrap(await servicesUtils.post(url, {}, HANDLE_ERROR))
        } catch (err) {
            console.log(`failed to relistMedia err: ${err}`)
            throw err
        }
    },
}
